import { STATUS_ICONS } from './../../models/conexoes.model';
import { ChangeDetectionStrategy, Component, Input, Output, EventEmitter, signal, computed, type SimpleChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { IconInduxtryComponent } from 'src/app/shared/components/common/icon-induxtry/icon-induxtry.component';
import { ButtonComponent } from 'src/app/shared/components/inputs/button/button.component';
import { AddSeriesModalComponent } from '../modals/add-series-modal/add-series-modal.component';
import {
  StartExecutionModalComponent,
  StartExecutionModalResult,
} from '../modals/start-execution-modal/start-execution-modal.component';
import { ToastrService } from 'ngx-toastr';
import { MatExpansionModule } from '@angular/material/expansion';
import { DetailConnectedItemService } from '../../services/detail-connected-item.service';
import { IconActionButtonComponent } from 'src/app/shared/components/common/icon-action-button/icon-action-button.component';
import { BBCE_SOURCE_ID, DatasetItem, JobExecution, SeriesItem } from '../../models/lakehouse.model';

@Component({
  selector: 'list-items-connected',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatTooltipModule,
    MatCheckboxModule,
    ButtonComponent,
    IconInduxtryComponent,
    MatExpansionModule,
    IconActionButtonComponent
  ],
  templateUrl: './list-items-connected.component.html',
  styleUrls: ['./list-items-connected.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ListItemsConnected {
  @Input() datasets: DatasetItem[] = [];
  @Input() seriesByDataset: Record<string, SeriesItem[]> = {};
  @Input() expandedDatasetId = '';
  @Input() loadingSeriesDatasetId = '';
  @Input() sourceSelectedId = '';

  @Output() loadSeries = new EventEmitter<string | { datasetId: string; expand?: boolean }>();
  @Output() toggleDetails = new EventEmitter<string>();
  @Output() seriesAdded = new EventEmitter<void>();
  @Output() seriesDelete = new EventEmitter<string>();
  @Output() executionsStarted = new EventEmitter<void>();

  private dialog = inject(MatDialog);
  private detailService = inject(DetailConnectedItemService);
  private toastr = inject(ToastrService);

  private _isSelected = signal<string>('');
  readonly selectionMode = signal<boolean>(false);
  readonly selectedSeriesIds = signal<Set<string>>(new Set());
  readonly selectedCount = computed(() => this.selectedSeriesIds().size);
  /** Permite vários accordions abertos ao mesmo tempo (necessário no fluxo Executar séries). */
  readonly expandedDatasetIds = signal<Set<string>>(new Set());

  readonly isExecuting = signal<boolean>(false);
  readonly seriesStatusIcon = STATUS_ICONS;
  readonly bbceSourceId = BBCE_SOURCE_ID;
  /** Datasets marcados no checkbox do accordion antes das séries serem carregadas. */
  private readonly pendingSelectAllDatasetIds = signal<Set<string>>(new Set());

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['sourceSelectedId']) {
      this.expandedDatasetIds.set(new Set());
      this.pendingSelectAllDatasetIds.set(new Set());
      if (this.selectionMode()) {
        this.exitSelectionMode();
      }
    }

    if (changes['expandedDatasetId'] && this.expandedDatasetId) {
      this.expandedDatasetIds.update(current => {
        const next = new Set(current);
        next.add(this.expandedDatasetId);
        return next;
      });
    }

    if (changes['seriesByDataset']) {
      this.applyPendingSelectAll();
      const lastSelectedSeries = sessionStorage.getItem('lastSelectedSeries');
      if (lastSelectedSeries) {
        this.onLoadSelectedSeries(lastSelectedSeries);
      }
    }
  }

  get isSelectedItemId(): string {
    return this._isSelected();
  }

  isDatasetExpanded(datasetId: string): boolean {
    return this.expandedDatasetIds().has(datasetId);
  }

  private hasSeriesLoaded(datasetId: string): boolean {
    return Object.prototype.hasOwnProperty.call(this.seriesByDataset, datasetId);
  }

  getSeries(datasetId: string): SeriesItem[] {
    return this.seriesByDataset[datasetId] ?? [];
  }

  getDatasetStatusIcon(dataset: DatasetItem): string | null {
    const series = this.getSeries(dataset._id);
    const statuses = series
      .map(s => s.last_job_execution?.status)
      .filter((status): status is JobExecution['status'] => !!status);

    if (statuses.length > 0) {
      if (statuses.every(status => status === 'completed')) return 'circle-success';
      if (statuses.some(status => status === 'failure')) return 'circle-cancel';
      if (statuses.some(status => status === 'outdated')) return 'update';
      if (statuses.some(status => status === 'processing' || status === 'initializing')) return 'tube-spinner';
      if (statuses.some(status => status === 'queued')) return 'queue';
      if (statuses.some(status => status === 'new' || status === 'notDone' || status === 'skip')) {
        return this.seriesStatusIcon.notDone;
      }
    }

    if (dataset.status && dataset.status in this.seriesStatusIcon) {
      return this.seriesStatusIcon[dataset.status as keyof typeof this.seriesStatusIcon];
    }

    return null;
  }

  onDatasetOpened(datasetId: string): void {
    this.expandedDatasetIds.update(current => {
      const next = new Set(current);
      next.add(datasetId);
      return next;
    });
    this.loadSeries.emit({ datasetId, expand: true });
  }

  onDatasetClosed(datasetId: string): void {
    this.expandedDatasetIds.update(current => {
      const next = new Set(current);
      next.delete(datasetId);
      return next;
    });
  }

  onItemClick(series: SeriesItem, event: Event): void {
    if (this.selectionMode()) {
      event.preventDefault();
      event.stopPropagation();
      this.toggleSelection(series);
    } else {
      this.onDetailsClick(series);
    }
  }

  onDetailsClick(item: SeriesItem): void {
    sessionStorage.setItem('lastSelectedSeries', item._id);
    this._isSelected.set(item._id);
    this.toggleDetails.emit(item._id);
  }

  onDeleteItemClick(series: SeriesItem, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.seriesDelete.emit(series._id);
  }

  enterSelectionMode(): void {
    this.selectionMode.set(true);
    this.selectedSeriesIds.set(new Set());
    this.pendingSelectAllDatasetIds.set(new Set());
  }

  exitSelectionMode(): void {
    this.selectionMode.set(false);
    this.selectedSeriesIds.set(new Set());
    this.pendingSelectAllDatasetIds.set(new Set());
  }

  toggleSelection(series: SeriesItem): void {
    const current = new Set(this.selectedSeriesIds());
    if (current.has(series._id)) {
      current.delete(series._id);
    } else {
      current.add(series._id);
    }
    this.selectedSeriesIds.set(current);
  }

  isSeriesSelected(series: SeriesItem): boolean {
    return this.selectedSeriesIds().has(series._id);
  }

  toggleAllInDataset(datasetId: string): void {
    if (!this.hasSeriesLoaded(datasetId)) {
      const pending = new Set(this.pendingSelectAllDatasetIds());
      if (pending.has(datasetId)) {
        pending.delete(datasetId);
        this.pendingSelectAllDatasetIds.set(pending);
        return;
      }

      pending.add(datasetId);
      this.pendingSelectAllDatasetIds.set(pending);
      this.loadSeries.emit({ datasetId, expand: false });
      return;
    }

    const ids = this.getSeries(datasetId).map(s => s._id);
    if (ids.length === 0) return;

    const current = new Set(this.selectedSeriesIds());
    const allSelected = ids.every(id => current.has(id));
    if (allSelected) {
      ids.forEach(id => current.delete(id));
    } else {
      ids.forEach(id => current.add(id));
    }
    this.selectedSeriesIds.set(current);
  }

  private applyPendingSelectAll(): void {
    const pending = this.pendingSelectAllDatasetIds();
    if (pending.size === 0) return;

    const stillPending = new Set(pending);
    const current = new Set(this.selectedSeriesIds());
    let changed = false;

    for (const datasetId of pending) {
      if (!this.hasSeriesLoaded(datasetId)) continue;
      this.getSeries(datasetId).forEach(series => current.add(series._id));
      stillPending.delete(datasetId);
      changed = true;
    }

    if (!changed) return;

    this.selectedSeriesIds.set(current);
    this.pendingSelectAllDatasetIds.set(stillPending);
  }

  isAllSelectedInDataset(datasetId: string): boolean {
    if (this.pendingSelectAllDatasetIds().has(datasetId)) return true;

    const ids = this.getSeries(datasetId).map(s => s._id);
    if (ids.length === 0) return false;
    const current = this.selectedSeriesIds();
    return ids.every(id => current.has(id));
  }

  selectAll(): void {
    const allIds = new Set<string>(this.selectedSeriesIds());
    const pending = new Set(this.pendingSelectAllDatasetIds());

    this.datasets.forEach(dataset => {
      if (!this.hasSeriesLoaded(dataset._id)) {
        pending.add(dataset._id);
        this.loadSeries.emit({ datasetId: dataset._id, expand: false });
        return;
      }
      this.getSeries(dataset._id).forEach(s => allIds.add(s._id));
    });

    this.pendingSelectAllDatasetIds.set(pending);
    this.selectedSeriesIds.set(allIds);
  }

  clearSelection(): void {
    this.selectedSeriesIds.set(new Set());
    this.pendingSelectAllDatasetIds.set(new Set());
  }

  areAllSelected(): boolean {
    const total = this.datasets.reduce(
      (acc, dataset) => acc + this.getSeries(dataset._id).length,
      0
    );
    if (total === 0) return false;
    return this.selectedSeriesIds().size === total;
  }

  toggleSelectAllGlobal(): void {
    if (this.areAllSelected()) {
      this.clearSelection();
    } else {
      this.selectAll();
    }
  }

  onRunExecutionsClick(event: unknown): void {
    const [, key] = Array.isArray(event) ? event : [null, null];
    if (key !== 'runExecutions') return;

    const ids = Array.from(this.selectedSeriesIds());
    if (ids.length === 0) return;

    this.openStartExecutionModal(ids);
  }

  private openStartExecutionModal(serieIds: string[]): void {
    const dialogRef = this.dialog.open(StartExecutionModalComponent, {
      width: '600px',
      maxHeight: '90vh',
      disableClose: true,
      data: {
        title: 'Executar séries',
        description: `Informe o intervalo de datas para executar ${serieIds.length} série(s) selecionada(s).`,
        seriesCount: serieIds.length,
      },
    });

    dialogRef.afterClosed().subscribe((result: StartExecutionModalResult | undefined) => {
      if (!result) return;
      this.executeSeriesBatch(serieIds, result);
    });
  }

  private executeSeriesBatch(serieIds: string[], range: StartExecutionModalResult): void {
    this.isExecuting.set(true);

    this.detailService.startSeriesBatch({
      serie_ids: serieIds,
      ...range,
    }).subscribe({
      next: () => {
        this.isExecuting.set(false);
        const successMessage = range.trigger_etl_dag === false
          ? `${serieIds.length} job(s) registrados sem disparar o pipeline.`
          : `${serieIds.length} execução(ões) iniciada(s) com sucesso.`;
        this.toastr.show(successMessage, 'Sucesso', { timeOut: 6000, messageClass: 'success' });
        this.executionsStarted.emit();
        this.exitSelectionMode();
      },
      error: (err) => {
        this.isExecuting.set(false);
        const msg = err?.error?.message ?? err?.message ?? 'Erro ao iniciar execução em lote';
        this.toastr.show(msg, 'Erro', { timeOut: 6000, messageClass: 'error' });
      },
    });
  }

  onLoadSelectedSeries(id: string): void {
    this._isSelected.set(id);
  }

  onAddSeriesClick(event: unknown): void {
    const [, key] = Array.isArray(event) ? event : [null, null];
    if (key === 'addSeries') {
      this.openAddSeriesModal();
    }
  }

  private openAddSeriesModal(): void {
    const addSeriesDialog = this.dialog.open(AddSeriesModalComponent, {
      width: '600px',
      maxHeight: '90vh',
      disableClose: true,
      data: {
        sourceId: this.sourceSelectedId
      }
    });

    addSeriesDialog.afterClosed().subscribe(result => {
      if (result && result.success) {
        this.seriesAdded.emit();
      }
    });
  }
}
